import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Platform } from 'react-native';
import type { SectionKey } from './proGroupsNavigationConfig';
import { SECTION_SUBTABS } from './proGroupsNavigationConfig';
import { getProGroupsLayoutMaxWidth } from '../../config/configManager';
import { useActiveAccountStore } from '../../stores/activeAccountStore';
import { resolveAuthContext } from '../../services/auth/authContext';

interface ProGroupsSubmenuBarProps {
  activeSection: SectionKey;
  activeSubsection: string;
  onSubsectionChange: (subtab: string) => void;
  onSectionChange?: (section: SectionKey) => void;
  onNavigateToSectionAndSubsection?: (section: SectionKey, subsection: string) => void;
  subtabs?: string[];
  storage?: any;
}

interface AccountOption {
  accountId: string;
  dname: string;
}

const resolveUserStore = (): any => resolveAuthContext().userStore;

const parseAccessTypeValue = (value: string | null | undefined): number => {
  const parsed = Number.parseInt((value || '').trim(), 10);
  return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY;
};

const getCurrentAccessType = (): number => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  const user = state?.session?.user as Record<string, unknown> | undefined;
  
  const accessTypeStr =
    (typeof user?.accessType === 'string' ? user.accessType : null)
    || (typeof user?.AccessType === 'string' ? user.AccessType : null)
    || null;
  
  return parseAccessTypeValue(accessTypeStr);
};

const getCurrentAccountId = (): string | null => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  const user = state?.session?.user as Record<string, unknown> | undefined;
  const familyAccount = state?.session?.familyAccount as Record<string, unknown> | undefined;

  return (
    (typeof user?.familyId === 'string' ? user.familyId : null)
    || (typeof familyAccount?.familyId === 'string' ? familyAccount.familyId : null)
    || (typeof familyAccount?.accountId === 'string' ? familyAccount.accountId : null)
    || (typeof familyAccount?.AccountId === 'string' ? familyAccount.AccountId : null)
    || null
  );
};

const getCurrentAccountName = async (storage?: any): Promise<string | null> => {
  const accountId = getCurrentAccountId();
  if (!accountId || !storage?.getItem) {
    return null;
  }

  try {
    const key = `account_prac_info_${accountId}`;
    const raw = await storage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return parsed?.AccountSettings?.PracInfo?.DName || null;
  } catch (error) {
    console.warn('ProGroupsSubmenuBar: Failed to get account name', error);
    return null;
  }
};

// Global registry for account selector options
const getAccountSelectorOptionsRegistry = (): { options: Array<{ accountId: string; dname: string }> } => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountSelectorOptions) {
    globalScope.__proGroupsAccountSelectorOptions = { options: [] };
  }
  return globalScope.__proGroupsAccountSelectorOptions;
};

export const ProGroupsSubmenuBar: React.FC<ProGroupsSubmenuBarProps> = ({
  activeSection,
  activeSubsection,
  onSubsectionChange,
  onSectionChange,
  onNavigateToSectionAndSubsection,
  subtabs,
  storage,
}) => {
  const resolvedSubtabs = subtabs ?? SECTION_SUBTABS[activeSection];
  const maxWidth = getProGroupsLayoutMaxWidth();
  const isWeb = Platform.OS === 'web';

  const [currentAccessType, setCurrentAccessType] = useState<number>(getCurrentAccessType());
  const [accountOptions, setAccountOptions] = useState<AccountOption[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isLoadingAccountName, setIsLoadingAccountName] = useState(false);
  const [accountNameLoaded, setAccountNameLoaded] = useState(false);
  
  // Use active account store instead of local state
  const activeAccountId = useActiveAccountStore(state => state.activeAccountId);
  const activeAccountName = useActiveAccountStore(state => state.activeAccountName);
  const setActiveAccount = useActiveAccountStore(state => state.setActiveAccount);
  const canSwitchAccount = useActiveAccountStore(state => state.canSwitchAccount);

  // Update accessType on mount and subscribe to changes
  useEffect(() => {
    const updateState = () => {
      setCurrentAccessType(getCurrentAccessType());
    };

    updateState();

    // Subscribe to user store changes
    const userStore = resolveUserStore();
    if (userStore?.subscribe) {
      const unsubscribe = userStore.subscribe(updateState);
      return unsubscribe;
    }
  }, []);

  // Load current account into options
  useEffect(() => {
    let cancelled = false;

    const loadCurrentAccount = async () => {
      if (currentAccessType >= 20 && currentAccessType < 30) {
        const accountId = getCurrentAccountId();
        if (!accountId) {
          return;
        }

        setIsLoadingAccountName(true);
        const accountName = await getCurrentAccountName(storage);
        
        if (cancelled) {
          return;
        }

        setIsLoadingAccountName(false);

        const currentAccount: AccountOption = {
          accountId: accountId,
          dname: accountName || `Account ${accountId}`,
        };

        // Start with just the current account
        // More accounts can be added from Manage Accounts search results
        setAccountOptions([currentAccount]);
        setAccountNameLoaded(true);

        // Dispatch event to notify that account name has loaded
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function' && accountName) {
          window.dispatchEvent(new CustomEvent('progroups:account-name-loaded', {
            detail: { accountId, accountName },
          }));
        }
      }
    };

    void loadCurrentAccount();

    return () => {
      cancelled = true;
    };
  }, [currentAccessType, storage]);

  // Listen for account selector options updates from ManageAccountsView
  useEffect(() => {
    const handleOptionsUpdate = async () => {
      const registry = getAccountSelectorOptionsRegistry();
      const searchOptions = registry.options || [];
      const currentAccountId = getCurrentAccountId();
      
      if (searchOptions.length > 0) {
        // Merge current account with search results (remove duplicates by accountId)
        const accountMap = new Map<string, AccountOption>();
        
        // Add search results first
        searchOptions.forEach((opt: AccountOption) => {
          accountMap.set(opt.accountId, opt);
        });
        
        // Ensure current account is always included with its loaded name
        if (currentAccountId) {
          if (!accountMap.has(currentAccountId)) {
            // If current account not in search results, load its name and add it
            const accountName = await getCurrentAccountName(storage);
            accountMap.set(currentAccountId, {
              accountId: currentAccountId,
              dname: accountName || `Account ${currentAccountId}`,
            });
          }
        }
        
        setAccountOptions(Array.from(accountMap.values()));
      }
    };

    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('progroups:account-selector-options-updated', handleOptionsUpdate);
      
      return () => {
        window.removeEventListener('progroups:account-selector-options-updated', handleOptionsUpdate);
      };
    }
  }, [storage]);

  const showAccountSelector = currentAccessType >= 20 && currentAccessType < 30;

  // Filter out 'Manage Accounts' from regular submenu tabs when in Account section
  const displaySubtabs = (showAccountSelector && activeSection === 'Account')
    ? resolvedSubtabs.filter(tab => tab !== 'Manage Accounts')
    : resolvedSubtabs;

  const handleAccountChange = (accountId: string) => {
    if (!canSwitchAccount) {
      console.warn('ProGroupsSubmenuBar: Cannot switch account - user does not have permission');
      return;
    }
    
    const selectedOption = accountOptions.find(opt => opt.accountId === accountId);
    const accountName = selectedOption?.dname || `Account ${accountId}`;
    
    // Update active account store (this persists to storage)
    setActiveAccount(accountId, accountName);
    setDropdownOpen(false);
    
    // Dispatch event to notify shell to refresh data
    if (typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('progroups:active-account-changed', {
        detail: { accountId, accountName, preloaded: false },
      }));
    }
    
    console.log('ProGroupsSubmenuBar: Active account changed to:', accountId, accountName);
  };

  const handleManageAccountsClick = () => {
    if (onNavigateToSectionAndSubsection) {
      // Use the combined navigation handler to atomically change section and subsection
      onNavigateToSectionAndSubsection('Account', 'Manage Accounts');
    } else {
      // Fallback to separate calls (legacy support)
      if (activeSection !== 'Account' && onSectionChange) {
        onSectionChange('Account');
      }
      onSubsectionChange('Manage Accounts');
    }
  };

  const webSelectStyle = {
    minWidth: 200,
    height: 32,
    border: '1px solid #cbd5e1',
    borderRadius: 6,
    padding: '0 8px',
    backgroundColor: '#ffffff',
    fontFamily: 'Arial',
    fontSize: 14,
    color: '#0f172a',
  } as const;

  return (
    <View style={styles.subnavShell}>
      <View style={[styles.subnavInner, { maxWidth }]}>
        {/* Account Selector Row - shown for accessType >= 20 and < 30 on all main tabs */}
        {showAccountSelector && (
          <View style={styles.accountSelectorRow}>
            <Text style={styles.accountSelectorLabel}>Select Account</Text>
            
            {isWeb ? (
              <select
                value={activeAccountId || ''}
                onChange={(e: any) => handleAccountChange(e.target.value)}
                style={webSelectStyle}
                disabled={isLoadingAccountName || accountOptions.length === 0 || !canSwitchAccount}
              >
                {accountOptions.map((option) => (
                  <option key={option.accountId} value={option.accountId}>
                    {option.dname}
                  </option>
                ))}
              </select>
            ) : (
              <Pressable
                style={styles.dropdownButton}
                onPress={() => setDropdownOpen(!dropdownOpen)}
                disabled={isLoadingAccountName || accountOptions.length === 0 || !canSwitchAccount}
              >
                <Text style={styles.dropdownText}>
                  {isLoadingAccountName
                    ? 'Loading...'
                    : (accountOptions.find(opt => opt.accountId === activeAccountId)?.dname || 'Select...')}
                </Text>
                <Text style={styles.dropdownArrow}>▼</Text>
              </Pressable>
            )}

            {/* Manage Accounts button - shows on all tabs when account selector is visible */}
            <Pressable
              onPress={handleManageAccountsClick}
              style={[
                styles.manageAccountsButton,
                activeSection === 'Account' && activeSubsection === 'Manage Accounts' && styles.manageAccountsButtonActive
              ]}
              accessibilityRole="button"
              accessibilityLabel="Manage Accounts"
            >
              <Text style={[
                styles.manageAccountsButtonText,
                activeSection === 'Account' && activeSubsection === 'Manage Accounts' && styles.manageAccountsButtonTextActive
              ]}>
                Manage Accounts
              </Text>
            </Pressable>

            {!isWeb && dropdownOpen && (
              <View style={styles.dropdownMenu}>
                {accountOptions.map((option) => (
                  <Pressable
                    key={option.accountId}
                    style={styles.dropdownMenuItem}
                    onPress={() => handleAccountChange(option.accountId)}
                  >
                    <Text style={styles.dropdownMenuItemText}>{option.dname}</Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Regular Submenu Row */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.subnavRow}
        >
          {displaySubtabs.map((subtab) => {
            const active = activeSubsection === subtab;
            return (
              <Pressable
                key={subtab}
                onPress={() => {
                  // Re-clicking the sub-menu tab that's already showing asks that view to
                  // refresh itself (e.g. Clients > Recent Reports / Open Admins / Manage Flags /
                  // Manage Messages re-run their list) — views that don't care ignore it.
                  if (active && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
                    window.dispatchEvent(new CustomEvent('progroups:subsection-reselected', {
                      detail: { section: activeSection, subsection: subtab },
                    }));
                  }
                  onSubsectionChange(subtab);
                }}
                style={[styles.subnavItem, active && styles.subnavItemActive]}
                accessibilityRole="button"
                accessibilityLabel={`Open ${subtab}`}
              >
                <Text style={[styles.subnavText, active && styles.subnavTextActive]}>
                  {subtab}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  subnavShell: {
    width: '100%',
    paddingTop: 14,
    paddingBottom: 8,
    paddingHorizontal: 20,
    backgroundColor: '#ffffff',
  },
  subnavInner: {
    width: '100%',
    alignSelf: 'center',
  },
  accountSelectorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  accountSelectorLabel: {
    fontSize: 13,
    fontFamily: 'Arial',
    fontWeight: '400',
    color: '#374151',
    marginRight: 12,
  },
  dropdownButton: {
    minWidth: 200,
    height: 30,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 2,
    backgroundColor: '#ffffff',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dropdownText: {
    fontSize: 13,
    fontFamily: 'Arial',
    color: '#0f172a',
    flex: 1,
  },
  dropdownArrow: {
    fontSize: 10,
    color: '#6b7280',
    marginLeft: 8,
  },
  dropdownMenu: {
    position: 'absolute',
    top: 42,
    left: 120,
    minWidth: 200,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 6,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
  },
  dropdownMenuItem: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
  },
  dropdownMenuItemText: {
    fontSize: 13,
    fontFamily: 'Arial',
    color: '#374151',
  },
  manageAccountsButton: {
    marginLeft: 16,
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  manageAccountsButtonActive: {
    borderBottomColor: '#1f6feb',
  },
  manageAccountsButtonText: {
    fontSize: 13,
    fontFamily: 'Arial',
    fontWeight: '400',
    color: '#374151',
  },
  manageAccountsButtonTextActive: {
    color: '#111827',
  },
  subnavRow: {
    flexGrow: 1,
    paddingHorizontal: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subnavItem: {
    marginHorizontal: 4,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  subnavItemActive: {
    borderBottomColor: '#1f6feb',
  },
  subnavText: {
    color: '#374151',
    fontSize: 13,
    fontFamily: 'Arial',
    fontWeight: '400',
  },
  subnavTextActive: {
    color: '#111827',
    fontWeight: '400',
  },
});
